import React, { createContext, useContext, useState } from 'react';

import type { Breadcrumb } from './breadcrumbs';

/* -------------------------------------------------------------------------------------------------
 * PageMetaContext
 * -----------------------------------------------------------------------------------------------*/
type PageMetaContextValue = {
  crumbs: Breadcrumb[];
  setCrumbs: React.Dispatch<React.SetStateAction<Breadcrumb[]>>;
};

const PageMetaContext = createContext<PageMetaContextValue | undefined>(
  undefined,
);

/**
 * Gets an instance of the page meta context
 *
 * @returns page meta context
 */
const usePageMeta = () => {
  const context = useContext(PageMetaContext);

  if (context == null) {
    throw new Error(
      'Component must be wrapped in PageMetaContext in order to access context.',
    );
  }

  return context;
};

/* -------------------------------------------------------------------------------------------------
 * PageMetaProvider
 * -----------------------------------------------------------------------------------------------*/
const PageMetaProvider: React.FunctionComponent<React.PropsWithChildren> = ({
  children,
}) => {
  /**
   * Init state to store crumbs
   *
   * Note: ideally we infer / configure crumbs through react-router
   * using https://reactrouter.com/en/main/hooks/use-matches#breadcrumbs.
   *
   * But we need the `queryClient` from react-query to be exposed outside of the react lifecycle (currently in useMemo)
   * so it can be used in react-router loaders.
   */
  const [crumbs, setCrumbs] = useState<Breadcrumb[]>([]);

  return (
    <PageMetaContext.Provider value={{ crumbs, setCrumbs }}>
      {children}
    </PageMetaContext.Provider>
  );
};

export { PageMetaProvider, usePageMeta };
